import { startOfDay, differenceInDays } from 'date-fns';
import { formatDate, getCleanedUrlParams, sortArray } from '@utils';
import { groupBy, pickBy, omit } from 'lodash-es';

import {
  BOARDS_ID,
  FILTER_RULE_ENUMS,
  FILTER_OPERATOR_ENUMS,
} from '@constants';
import {
  BUY_OUT_REASONS_OF_REJECTION,
  BUY_OUT_TASK_STATUSES_NAMES,
} from 'components/buyout/constants';

export const getBuyOutRequestPayload = (
  payload = {},
  modalFiltersRules = null
) => {
  const params =
    omit(payload, ['page', 'pageSize', 'order', 'orderBy', 'query']) || {};
  const fields = pickBy(
    params,
    (item) => item !== null && item !== undefined && item !== ''
  );
  const numberFields = [];

  const multyFilterStringFields = [];
  const multyFilterNumberFields = ['status'];

  const _getValue = ({ field }) => {
    if (multyFilterStringFields.includes(field)) {
      return payload[field]?.split(',');
    }
    if (multyFilterNumberFields.includes(field)) {
      return payload[field]?.split(',').map((item) => Number(item));
    }
    if (field === 'assignee' && payload.assignee === 'None') {
      return '';
    }

    return numberFields.includes(field)
      ? Number(payload[field])
      : payload[field];
  };
  const _getType = ({ field }) => {
    if (
      multyFilterStringFields.includes(field) ||
      multyFilterNumberFields.includes(field)
    ) {
      return FILTER_RULE_ENUMS.IsAnyOfRuleType;
    }
    if (field === 'assignee' && payload.assignee === 'None') {
      return FILTER_RULE_ENUMS.isEmpty;
    }
    return FILTER_RULE_ENUMS.ComparableRuleType;
  };

  const value = [
    ...Object.keys(fields).map((field) => ({
      field,
      operator: FILTER_OPERATOR_ENUMS['='],
      type: _getType({ field }),
      value: _getValue({ field }),
    })),
  ];

  value.push(...(modalFiltersRules?.value || []));

  if (value.length) {
    return {
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value,
    };
  }

  return {
    type: FILTER_RULE_ENUMS.EmptyRuleType,
  };
};

const BUYOUT_LIMIT = 500;

const INSTALLMENTS_MONTHS = ['12', '24', '36', '48', '60', '72', '84'];

function getChangedItems(originalArray, currentArray) {
  const changed = [];

  for (let i = 0; i < currentArray.length; i++) {
    if (JSON.stringify(originalArray[i]) !== JSON.stringify(currentArray[i])) {
      changed.push(currentArray[i]);
    }
  }

  return changed;
}

const normalizeInstallments = (backendInstallments = []) => {
  return INSTALLMENTS_MONTHS.map((months) => {
    const existing = backendInstallments?.find((i) => i.months === months);

    if (existing) {
      return {
        months: existing.months,
        selected: Boolean(existing.selected),
        amount: {
          number: existing.amount?.number ?? '',
          currency: existing.amount?.currency || 'EUR',
        },
      };
    }

    // empty installment
    return {
      months,
      selected: false,
      amount: {
        number: '',
        currency: 'EUR',
      },
    };
  });
};

const computeOfferRequestColor = (offerRequestDate) => {
  if (offerRequestDate) {
    const given = new Date(offerRequestDate);
    const current = startOfDay(new Date());
    const dayDiff = differenceInDays(current, given);
    if (dayDiff >= 35) {
      return '#FFA366';
    }
    return '#03AA46';
  }
  if (!offerRequestDate) {
    return '#FF0000';
  }
};

const handleAmountInstallmentsItem = (newAmountItem) => {
  if (newAmountItem.Key === 'amount') {
    return {
      ...newAmountItem,
      Value: newAmountItem.Value.map((amountValueItem) => ({
        ...amountValueItem,
        Value:
          amountValueItem.Key === 'number'
            ? amountValueItem.Value / 100
            : amountValueItem.Value,
      })),
    };
  }
  return newAmountItem;
};

const normalizeBuyOutData = (data, assignees) => {
  if (!data?.length) {
    return {};
  }
  let totalFinalPrice = 0;

  const assigneesMap = assignees.reduce((acc, cur) => {
    acc[cur.value] = cur;
    return acc;
  }, {});

  const tasks = [];
  data?.forEach((item) => {
    const assignee = assigneesMap[item.assignee] || {};

    if (item?.customFields?.finalPrice?.number) {
      totalFinalPrice += Number(item?.customFields?.finalPrice?.number);
    }
    const newTask = {
      ...item,
      offerRequestDateColor: computeOfferRequestColor(
        item?.customFields?.offerRequestDate
      ),
      customFields: {
        ...item.customFields,
        billedAt: item?.customFields?.billedAt
          ? formatDate(item?.customFields?.billedAt, 'dd/MM/yyyy')
          : '-',
        finalPrice: item?.customFields?.finalPrice?.number
          ? new Intl.NumberFormat('el-GR', {
              style: 'currency',
              currency: 'EUR',
            }).format(item?.customFields?.finalPrice?.number)
          : '-',
        offerSentDate: item?.customFields?.offerSentDate
          ? formatDate(item?.customFields?.offerSentDate, 'dd/MM/yyyy')
          : '-',
        approvalStatus: !item?.customFields?.approvalStatus
          ? 'Pending'
          : item?.customFields?.approvalStatus,

        loan: item?.customFields?.loan,
        loanContractId: item?.customFields?.loanContractId,
      },
      href: `/buyout/${item?.id}`,
      status: item?.status,
      assignee,
      description: item?.name,
      date: item.createdAt ? formatDate(item.createdAt, 'dd/MM/yyyy') : null,
      createdAt: item?.createdAt
        ? formatDate(item?.createdAt, 'dd/MM/yyyy')
        : '-',
      userId: item?.customFields?.userId,
      title: item?.customFields?.vehiclePlate || '',
    };

    tasks.push(newTask);
  });
  return {
    boardId: BOARDS_ID?.BUY_OUT,
    tasks,
    totalFinalPrice: new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
    }).format(totalFinalPrice),
  };
};

const getBuyOutsColumnsState = (data) => {
  const { tasks } = data || {};
  const order = [0, 4, 5, 7, 6, 8, 9, 10, 3, 11, 12];
  const groupedByStatus = groupBy(tasks, 'status');

  const columns = order.reduce((acc, cur) => {
    acc.push({
      id: cur,
      title: BUY_OUT_TASK_STATUSES_NAMES[cur].label,
      cards: groupedByStatus[cur] || [],
      styles: BUY_OUT_TASK_STATUSES_NAMES[cur].styles,
      value: true,
      label: BUY_OUT_TASK_STATUSES_NAMES[cur].label,
      key: cur,
    });
    return acc;
  }, []);

  return columns;
};

const normalizeBuyOutTaskData = (data) => {
  if (!data) {
    return {};
  }
  const changelog = data?.changelog?.map((item) => {
    const normalizedNewValue =
      item.fieldName === 'installments'
        ? item.newValue.map((newValue) =>
            newValue.map(handleAmountInstallmentsItem)
          )
        : item.newValue;

    return { ...item, newValue: normalizedNewValue };
  });

  const hasFinalPriceRejectionReason =
    data?.customFields?.finalPriceRejectionReason &&
    BUY_OUT_REASONS_OF_REJECTION.find(
      (item) => item.value === data?.customFields?.finalPriceRejectionReason
    );
  const task = {
    ...data,
    changelog: changelog?.reverse(),
    comments: sortArray(data?.comments, 'createdAt', 'desc')?.map((entry) => {
      return {
        id: entry?.id,
        date: entry?.createdAt
          ? formatDate(entry?.createdAt, 'dd MMMM yyyy')
          : '-',
        time: entry?.createdAt ? formatDate(entry?.createdAt, 'HH:mm') : '-',
        name: entry?.userFullName || entry?.user || '-',
        comment: entry?.comment,
        edit: entry?.editedAt
          ? formatDate(entry?.editedAt, 'dd MMMM yyyy HH:mm')
          : '',
      };
    }),
    documents: data?.documents?.map((document) => {
      return { ...document, cannotDelete: document?.type === 'BuyoutOffer' };
    }),
    customFields: {
      ...data.customFields,
      clientReasonOfRejection: data?.customFields?.clientReasonOfRejection
        ? data?.customFields?.clientReasonOfRejection?.map((item) => ({
            text: item,
            value: item,
          }))
        : [],
      finalPriceRejectionReason: !hasFinalPriceRejectionReason
        ? 'Other'
        : data.customFields.finalPriceRejectionReason,
      finalPriceRejectionReasonOther: !hasFinalPriceRejectionReason
        ? data.customFields.finalPriceRejectionReason
        : '',
      vehiclePurchasePrice: data.customFields?.vehiclePurchasePrice
        ? data.customFields?.vehiclePurchasePrice?.number
        : '',
      extraCap: data.customFields?.extraCap
        ? data.customFields?.extraCap?.number
        : '',
      commercialAssessment: data.customFields?.commercialAssessment
        ? data.customFields?.commercialAssessment?.number
        : '',
      marginPercentage: data.customFields?.marginPercentage?.toFixed(2),
      transferOfOwnershipFee: data.customFields?.transferOfOwnershipFee
        ? data.customFields?.transferOfOwnershipFee?.number
        : null,
      finalPrice: data.customFields?.finalPrice
        ? data.customFields?.finalPrice?.number
        : null,
      financedAmount: data.customFields?.financedAmount
        ? data.customFields?.financedAmount?.number
        : null,
      administrativeExpenses: data.customFields?.administrativeExpenses
        ? data.customFields?.administrativeExpenses?.number
        : null,
      downpayment: data.customFields?.downpayment
        ? data.customFields?.downpayment?.number
        : null,
      comparablePrice: data.customFields?.comparablePrice
        ? data.customFields?.comparablePrice?.number
        : null,
      minimumPrice: data.customFields?.minimumPrice
        ? data.customFields?.minimumPrice?.number
        : '',
      nbv: data.customFields?.nbv ? data.customFields?.nbv?.number : '',
      offerSentDate: formatDate(
        data?.customFields?.offerSentDate,
        'dd-MM-yyyy'
      ),
      offerExpirationDate: formatDate(
        data?.customFields?.offerExpirationDate,
        'dd-MM-yyyy'
      ),
      offerRequestDate: formatDate(
        data?.customFields?.offerRequestDate,
        'dd-MM-yyyy'
      ),
      installments: normalizeInstallments(data?.customFields?.installments),
    },
  };
  return task;
};

const normalizeVehicleData = (data = {}) => {
  const properties = [];

  if (data.properties?.fuelType) {
    properties.push(data.properties.fuelType);
  }
  if (data.properties?.transmission) {
    properties.push(data.properties.transmission);
  }
  if (data.properties?.color) {
    properties.push(data.properties.color);
  }
  if (data.year) {
    properties.push(data.year);
  }

  return {
    internalId: data.internalId,
    id: data.id,
    title: [data.manufacturer, data.model, data.edition]?.join(' '),
    plate: data.plate,
    image: data?.images?.[0]?.url,
    properties,
    vin: data.vin,
    procurement: data.procurement,
  };
};

const getBuyoutPayload = (values = {}, task = {}) => {
  const formInstallments = values.customFields.installments;

  const isInstallmentsDirty =
    JSON.stringify(formInstallments) !==
    JSON.stringify(task.customFields.installments);
  const changedInstallments = getChangedItems(
    task.customFields.installments,
    formInstallments
  );

  // if the initial installments have not been changed, we should not send them as changed
  const installments = isInstallmentsDirty
    ? changedInstallments.map((instalment) => ({
        months: instalment.months,
        selected: instalment.selected,
        amount: instalment.amount.number,
      }))
    : [];

  return {
    assignee: values.assignee?.value || task.assignee,
    customFields: {
      ...omit(values.customFields, [
        'offerRequestDate',
        'offerSentDate',
        'offerExpirationDate',
        'finalPriceRejectionReasonOther',
      ]),
      finalPriceRejectionReason:
        values.customFields.finalPriceRejectionReasonOther ||
        values.customFields.finalPriceRejectionReason,
      minimumPrice: values.customFields?.minimumPrice || undefined,
      comparablePrice: values.customFields?.comparablePrice || undefined,
      vehiclePurchasePrice:
        values.customFields?.vehiclePurchasePrice || undefined,
      finalPrice: values.customFields?.finalPrice || undefined,
      marginPercentage: values.customFields?.marginPercentage || undefined,
      nbv: values.customFields?.nbv || undefined,
      extraCap: values.customFields?.extraCap || undefined,
      commercialAssessment:
        values.customFields?.commercialAssessment || undefined,
      interestRate: values.customFields?.interestRate
        ? parseFloat(values.customFields.interestRate)
        : undefined,
      clientReasonOfRejection: values.customFields?.clientReasonOfRejection
        ?.length
        ? values.customFields.clientReasonOfRejection.map((item) => item.value)
        : undefined,
      approvalStatus:
        values.customFields?.approvalStatus === 'Pending'
          ? undefined
          : values.customFields?.approvalStatus,
      transferChecklist: {
        ...values.customFields?.transferChecklist,
        copyOfLicenseSending: !values?.customFields.loan
          ? undefined
          : values.customFields?.transferChecklist?.copyOfLicenseSending,
      },
      installments,
    },
  };
};

const getBuyOutParamsAndPayload = ({ order, orderBy, query, others }) => {
  const payload = getBuyOutRequestPayload({
    boardId: BOARDS_ID.BUY_OUT,
    ...others,
  });

  const params = getCleanedUrlParams({
    order,
    orderBy,
    query,
  });

  const exportParams = getCleanedUrlParams({
    order,
    orderBy,
    query,
  });

  return { exportParams, params, payload };
};

export {
  BUYOUT_LIMIT,
  normalizeBuyOutData,
  getBuyOutsColumnsState,
  normalizeBuyOutTaskData,
  normalizeVehicleData,
  getBuyoutPayload,
  getBuyOutParamsAndPayload,
};
